import React, { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Card from "components/card";
import Select, {
  DropdownIndicatorProps,
  GroupBase,
  SingleValue,
  StylesConfig,
  ValueContainerProps,
} from "react-select";
import eyeview from "../../../../assets/svg/eyeView.svg";
import completedRide from "../../../../assets/svg/completedRide.svg";
import cancelRide from "../../../../assets/svg/cancelRide.svg";
import ongoingRide from "../../../../assets/svg/ongoingRide.svg";
import filterIcon from "../../../../assets/svg/filterIcon.svg";
import arrow_down from "../../../../assets/svg/arrow_down.svg";
import whatsApp_logo from "../../../../assets/images/Logo-WhatsApp.png";
import android_logo from "../../../../assets/images/android_logo.png";
import "../rides.css";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
import { useNavigate } from "react-router-dom";
import { FaCaretDown, FaCaretUp } from "react-icons/fa";
import { formatCurrency } from "services/customAPI";
import { RideFilterEnum, RideTableRowDTO } from "types/api.types";
import { RideStatusOption } from "types/types";

type RowObj = RideTableRowDTO;

type RideStatusGroup = GroupBase<RideStatusOption>;

type ColumnsTableProps = {
  tableData: RideTableRowDTO[];
  statusOptions: RideStatusOption[];
  setRideStatus: (val: RideFilterEnum) => void;
  rideStatus: RideFilterEnum;
};

type RideDropdownIndicatorProps = DropdownIndicatorProps<
  RideStatusOption,
  false,
  RideStatusGroup
>;

type RideValueContainerProps = ValueContainerProps<
  RideStatusOption,
  false,
  RideStatusGroup
>;

function ColumnsTable(props: ColumnsTableProps) {
  const { tableData, statusOptions, setRideStatus, rideStatus } = props;
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const columns = [
    columnHelper.accessor("platform", {
      id: "logo",
      header: () => (
        <p className="text-sm font-bold text-gray-300">
          {t("Source")}
        </p>
      ),
      cell: (info) => {
        const platform = info.getValue();
        return (
          <p className="text-sm font-bold text-white">
            {platform === "whatsApp" ? (
              <img
                src={whatsApp_logo}
                alt="whatsApp_logo"
                width={30}
                height={30}
              />
            ) : (
              <img src={android_logo} alt="android_logo" width={30} height={30} />
            )}
          </p>
        );
      },
    }),
    columnHelper.accessor("bookingDate", {
      id: "bookingDate",
      header: () => (
        <p className="text-sm font-bold text-gray-300">
          {t("Booking Date")}
        </p>
      ),
      cell: (info) => {
        const bookingDate = info.getValue();
        return (
          <p className="text-sm font-bold text-white">
            {bookingDate ?? "N/A"}
          </p>
        );
      },
    }),
    columnHelper.accessor("bookingTime", {
      id: "bookingTime",
      header: () => (
        <p className="text-sm font-bold text-gray-300">
          {t("Booking Time")}
        </p>
      ),
      cell: (info) => {
        const bookingTime = info.getValue();
        return (
          <p className="text-sm font-bold text-white">
            {bookingTime ?? "N/A"}
          </p>
        );
      },
    }),
    columnHelper.accessor("riderMobileNum", {
      id: "riderMobileNum",
      header: () => (
        <p className="text-sm font-bold text-gray-300">
          {t("Rider Mobile No.")}
        </p>
      ),
      cell: (info) => {
        const riderMobileNum = info.getValue();
        const isClickable = !!riderMobileNum && riderMobileNum !== "N/A";
        return (
          <p
            className={`text-sm font-bold text-white ${isClickable ? 'cursor-pointer hover:text-cyan-400 hover:underline transition-colors' : ''}`}
            onClick={(e) => {
              if (isClickable) {
                e.stopPropagation();
                navigate(`/admin/riders?search=${encodeURIComponent(riderMobileNum)}`);
              }
            }}
          >
            {riderMobileNum ?? "N/A"}
          </p>
        );
      },
    }),
    columnHelper.accessor("DriverMobileNum", {
      id: "DriverMobileNum",
      header: () => (
        <p className="text-sm font-bold text-gray-300">
          {t("Driver Mobile No.")}
        </p>
      ),
      cell: (info) => {
        const driverMobileNum = info.getValue();
        const driverId = info.row.original.driverId;
        const isClickable = !!(driverMobileNum && driverId);
        return (
          <p
            className={`text-sm font-bold text-white ${isClickable ? 'cursor-pointer hover:text-cyan-400 hover:underline transition-colors' : ''}`}
            onClick={(e) => {
              if (isClickable) {
                e.stopPropagation();
                navigate(`/admin/drivers/driverform/${driverId}`);
              }
            }}
          >
            {driverMobileNum ?? "N/A"}
          </p>
        );
      },
    }),
    columnHelper.accessor("fare", {
      id: "fare",
      header: () => (
        <p className="text-sm font-bold text-gray-300">
          {t("Fare")}
        </p>
      ),
      cell: (info) => {
        const fare = info.getValue();
        return (
          <p className="text-sm font-bold text-white">
            {typeof fare === "number" ? formatCurrency(fare) : "N/A"}
          </p>
        );
      },
    }),
    columnHelper.accessor("status", {
      id: "status",
      header: () => (
        <p className="text-sm font-bold text-gray-300">
          {t("Status")}
        </p>
      ),
      cell: (info) => {
        const status = info.getValue();
        const statusText = typeof status === "string" ? status : "";
        return (
          // <div className="flex items-center">
          //   {getStatusImage(info.row.original.status)}
          //   <p className="ml-2 text-sm font-bold text-white">
          //     {info.getValue()}
          //   </p>
          // </div>
          <div className="flex items-center">
            {statusText === "Completa" ? (
              <img src={completedRide} height={25} width={25} alt="Completed" />
            ) : statusText === "Cancelada" || statusText === "Failed" ? (
              <img src={cancelRide} height={25} width={25} alt="Canceled" />
            ) : (
              <img
                src={ongoingRide}
                height={21}
                width={21}
                alt="Ongoing"
                className="me-1"
              />
            )}
            <p className="text-sm font-bold text-white">
              {" "}
              {statusText.charAt(0).toUpperCase() + statusText.slice(1)}
              {/* {info.getValue()} */}
            </p>
          </div>
        );
      },
    }),
    columnHelper.accessor("origin", {
      id: "origin",
      header: () => (
        <p className="text-sm font-bold text-gray-300">
          {t("Origin")}
        </p>
      ),
      cell: (info) => {
        const origin = info.getValue();
        return (
          <p className="text-sm font-bold text-white">
            {origin ? `${origin.slice(0, 15)}...` : "N/A"}
          </p>
        );
      },
    }),
    columnHelper.accessor("destination", {
      id: "destination",
      header: () => (
        <p className="text-sm font-bold text-gray-300">
          {t("Destination")}
        </p>
      ),
      cell: (info) => {
        const destination = info.getValue();
        return (
          <p className="text-sm font-bold text-white">
            {destination ? `${destination.slice(0, 15)}...` : "N/A"}
          </p>
        );
      },
    }),
    columnHelper.accessor("view", {
      id: "view",
      header: () => (
        <p className="text-sm font-bold text-gray-300">
          {t("Ver")}
        </p>
      ),
      cell: (info) => (
        // <p className="text-sm font-bold text-navy-700 dark:text-white">
        //   <img src={eyeview} height={30} width={30} />
        // </p>

        <div className="flex items-center">
          <img
            src={eyeview}
            height={30}
            width={30}
            style={{ cursor: "pointer" }}
            alt="Ver detalhes"
            onClick={() =>
              navigate(`/admin/rides/ridedetails/${info.getValue()}`)
            }
          />
        </div>
      ),
    }),
  ]; // eslint-disable-next-line
  const [data, setData] = React.useState<RowObj[]>([...tableData]);
  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    debugTable: true,
  });

  const filter = ({ children, innerProps }: RideValueContainerProps) => (
    <div
      className="flex items-center justify-between"
      style={{ cursor: "pointer" }}
      {...innerProps}
    >
      <img
        src={filterIcon}
        className="ml-3"
        height={20}
        width={20}
        alt="Filtro"
      />
      {children}
    </div>
  );

  const arrowdown = ({ innerProps }: RideDropdownIndicatorProps) => (
    <div
      className="flex items-center justify-between"
      style={{ cursor: "pointer" }}
      {...innerProps}
    >
      <img
        src={arrow_down}
        className="mr-3"
        height={15}
        width={15}
        alt="Abrir"
      />
    </div>
  );

  const handleStatusChange = useCallback(
    (option: SingleValue<RideStatusOption>): void => {
      if (!option) {
        return;
      }
      setRideStatus(option.value);
    },
    [setRideStatus]
  );

  const selectedOption =
    statusOptions.find((option) => option.value === rideStatus) ?? null;

  const selectStyles: StylesConfig<RideStatusOption, false, RideStatusGroup> = {
    menu: (provided) => ({ ...provided, zIndex: 9999, backgroundColor: "#1B2559", border: "1px solid #334155" }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? "#0E7490" : "#1B2559",
      cursor: "pointer",
      color: "white",
      "&:hover": {
        backgroundColor: "#164e63",
      },
    }),
    singleValue: (provided) => ({ ...provided, color: "white" }),
    control: (provided) => ({ ...provided, backgroundColor: "#1B2559", borderColor: "#334155", minHeight: "38px" }),
  };

  useEffect(() => {
    setData([...tableData]);
  }, [tableData]);

  return (
    <Card extra={"w-full pb-10 p-4 h-full"}>
      <header className="relative flex items-center justify-between">
        <div className="text-xl font-bold text-white">
          {t("Rides")}
        </div>
        <div style={{ width: "200px" }}>
          <Select
            isSearchable={false}
            // className="w-400"
            className="custom-select"
            // options={statusOptions}
            options={statusOptions.map((option) => ({
              // Map over statusOptions to translate labels
              ...option,
              label: t(`${option.label}`), // Translate label
              // value: t(`${option.label}`), // Translate value
            }))}
            onChange={handleStatusChange}
            value={selectedOption}
            // value={statusOptions.find(option => option.value === rideStatus)} // Use find instead of filter
            name="rideStatus"
            // defaultValue={{ label: defaultLabel, value: rideStatus }} // Set the translated default value
            styles={selectStyles}
            components={{
              DropdownIndicator: arrowdown,
              IndicatorSeparator: () => null,
              ValueContainer: filter,
            }}
          />
        </div>
        {/* <CardMenu /> */}
      </header>

      <div className="mt-8 overflow-x-scroll xl:overflow-x-hidden">
        <table className="w-full">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="border-b border-white/10">
                {headerGroup.headers.map((header) => {
                  return (
                    <th
                      key={header.id}
                      colSpan={header.colSpan}
                      onClick={header.column.getToggleSortingHandler()}
                      className="cursor-pointer pb-3 pr-4 pt-4 text-start"
                    >
                      <div className="flex items-center justify-between text-xs text-gray-200">
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                        {/* {{
                          asc: "",
                          desc: "",
                        }[header.column.getIsSorted() as string] ?? null} */}
                        {
                          <>
                            {header.column.getIsSorted() === "asc" ? (
                              <FaCaretUp
                                className="mr-[-6] font-bold text-gray-600"
                                size={20}
                              />
                            ) : header.column.getIsSorted() === "desc" ? (
                              <FaCaretDown
                                size={20}
                                className="font-bold text-gray-600"
                              />
                            ) : (
                              <div className="mr-[-6] flex">
                                <FaCaretDown
                                  size={20}
                                  className="font-bold text-gray-600"
                                />
                              </div>
                            )}
                          </>
                        }
                      </div>
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          {tableData.length === 0 ? (
            <tbody>
              <tr>
                <td colSpan={columns.length} style={{ textAlign: "center" }}>
                  <h2 className="m-4 text-gray-400" style={{ fontSize: "30px" }}>
                    {t("No Results!")}
                  </h2>
                </td>
              </tr>
            </tbody>
          ) : (
            <tbody>
              {table
                .getRowModel()
                .rows?.slice(0, 10)
                .map((row, idx) => {
                  return (
                    <tr
                      key={row.id}
                      className={`transition-colors hover:bg-white/5 ${idx % 2 === 0 ? 'bg-white/[0.02]' : ''
                        }`}
                    >
                      {row.getVisibleCells().map((cell) => {
                        return (
                          <td
                            key={cell.id}
                            className="min-w-[11.1%] border-b border-white/5 py-3.5 pr-4 text-start"
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
            </tbody>
          )}
        </table>
      </div>
    </Card>
  );
}

export default ColumnsTable;
const columnHelper = createColumnHelper<RowObj>();
